/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import _, { trimEnd } from 'lodash';
import { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import keyValuePairTableStyles, { themekeyValuePairTableStyles } from './KeyValuePairTable.styles';

const CHILDREN_PLACEHOLDER_ID = 'children';

interface TableItem {
    key?: string;
    valueType: string;
    value: string;
    markerLabel?: string;
    showToolTip?: boolean;
    tooltipOptions: { backgroundColor: string; text: string; iconName: string };
    uniqueListItemId?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    tableItems?: TableItem[];
    tableTitles?: Headings[];
    transformData?: boolean;
    functionToTransformData?: string;
    typographyClass?: string;
    disableTabStaticData?: boolean;
}

const KeyValuePairTable = (props: commonTypes.WidgetInstanceProps) => {
    const { Placeholder } = commonHooks.useAppContext();
    const data: any = props.data;
    const theme = data?.themeSelected || props?.theme;
    const i18n = commonHooks.useLocalization();
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        tableItems,
        tableTitles,
        transformData,
        functionToTransformData,
        disableTabStaticData,
    } = widgetProperties || props;
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const annotator = commonHooks.useAnnotator();
    const [isMounted, setIsMounted] = useState(false);
    const tabIndex = globalDisableTabStaticData || disableTabStaticData ? -1 : 0;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    let isRender = {
        title: true,
        table: true,
        widget: true,
        transformedTableItems: tableItems,
        transformedTableData: data,
        hideTitlesMsg: '',
        hideWidgetMsg: '',
        hideTableMsg: '',
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedResult =
            transformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransformData, {
                tableItems,
                data,
                annotator,
                handlebar: i18n.getHandlebars(),
            });

        if (transformedResult) {
            isRender = { ...isRender, ...transformedResult };
        }
    } catch (e) {
        console.log(e);
    }

    const _data = isRender?.transformedTableData;

    return (
        <>
            {isRender?.widget ? (
                <div
                    className={`key-value-table ${isRender?.table ? 'show' : 'hide'} ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${!theme
                            ? keyValuePairTableStyles
                            : themekeyValuePairTableStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    {isRender?.title ? (
                        <HeaderWrapper
                            headings={tableTitles}
                            data={_data}
                            disableTabStaticData={
                                globalDisableTabStaticData || disableTabStaticData
                            }
                        />
                    ) : (
                        <p
                            className="hide-title-message"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(isRender?.hideTitlesMsg, _data),
                            }}
                        />
                    )}
                    {isRender?.table ? (
                        <ul className="list">
                            {isRender?.transformedTableItems?.map((p: TableItem, index: number) => {
                                let markers: any = '';

                                try {
                                    markers =
                                        !_.isEmpty(annotator) &&
                                        annotator?.markersByMarkerTargetId(`${p?.markerLabel}`);
                                } catch (e) {
                                    console.log(e);
                                }

                                const tooltipText = i18n.format(
                                    p?.tooltipOptions?.text || '',
                                    _data
                                );

                                return (
                                    <li className="list-item" key={index} id={p.uniqueListItemId}>
                                        <div
                                            className="list-key"
                                            tabIndex={tabIndex}
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(p.key || '', {
                                                    ..._data,
                                                    _markers: markers,
                                                }),
                                            }}
                                        />
                                        <div className="list-value">
                                            {p.valueType === 'String' ? (
                                                <span
                                                    tabIndex={tabIndex}
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            trimEnd(i18n.format(p.value, _data)) ||
                                                            i18n.t('', '_', 'dash'),
                                                    }}
                                                />
                                            ) : (
                                                <Placeholder
                                                    // eslint-disable-next-line max-len
                                                    contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}--${p?.uniqueListItemId}`}
                                                    parentWidgetInstanceId={widgetInstance?.id}
                                                    data={data}
                                                />
                                            )}

                                            {p.showToolTip && (
                                                <div className="tooltip-container">
                                                    <i
                                                        className={p.tooltipOptions.iconName}
                                                        data-tip
                                                        // eslint-disable-next-line max-len
                                                        data-for={`tooltip-${index}-key${props.widgetInstanceId}`}
                                                    />
                                                    {isMounted && (
                                                        <ReactTooltip
                                                            // eslint-disable-next-line max-len
                                                            id={`tooltip-${index}-key${props.widgetInstanceId}`}
                                                            textColor="#fff"
                                                            backgroundColor={
                                                                p.tooltipOptions.backgroundColor
                                                            }
                                                        >
                                                            <span
                                                                tabIndex={tabIndex}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: tooltipText,
                                                                }}
                                                            />
                                                        </ReactTooltip>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p
                            className="hide-table-message"
                            tabIndex={tabIndex}
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(isRender?.hideTableMsg, _data),
                            }}
                        />
                    )}
                </div>
            ) : (
                <p
                    className="hide-widget-message"
                    tabIndex={tabIndex}
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(isRender?.hideWidgetMsg, _data),
                    }}
                />
            )}
        </>
    );
};

export default KeyValuePairTable;
